import { RADIUS } from "@elyxeer/ui";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

import { useLocale } from "~/i18n";
import type {
  EventReminderAdmin,
  EventReminderKind,
  EventReminderStatus,
} from "~/types/events-admin";

/**
 * The reminder ladder, in milliseconds. A copy of `REMINDER_OFFSETS` in
 * `services/event_reminders.py`, and only ever used to *preview* what the server will
 * compute on the next save — the rendered times of saved rows always come from the
 * server, so a drift between the two tables shows up as a preview that does not match
 * the row it replaced rather than as a wrong schedule.
 */
const REMINDER_OFFSET_MS: Record<EventReminderKind, number> = {
  t7d: 7 * 24 * 60 * 60 * 1000,
  t1d: 24 * 60 * 60 * 1000,
  t1h: 60 * 60 * 1000,
};

const STATUS_COLOR: Record<
  EventReminderStatus,
  "default" | "info" | "success" | "warning"
> = {
  scheduled: "default",
  sending: "info",
  sent: "success",
  // Not an error: a leg of an event created three hours out is legitimately past.
  skipped: "warning",
};

const REMINDER_TIME_FORMAT: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
};

/**
 * Whether this leg already reached somebody, and is therefore history no edit can move.
 *
 * Mirrors `_has_fired` in `services/event_reminders.py` through the fields the wire
 * actually carries: `delivered_at` is the in-app delivery record, and the two live
 * statuses cover a push fan-out that started before anything was stamped.
 */
function hasFired(reminder: EventReminderAdmin): boolean {
  return (
    reminder.delivered_at !== null ||
    reminder.status === "sent" ||
    reminder.status === "sending"
  );
}

/**
 * The ladder grouped by leg, server order preserved between groups and inside one.
 *
 * There is no one-row-per-kind rule to lean on: `read_event_reminders_admin` returns
 * every row for the `(slug, locale)` pair, and a postponement past the reschedule
 * threshold deliberately keeps the leg that already fired alongside the one it re-arms.
 */
function groupByKind(
  reminders: EventReminderAdmin[],
): [EventReminderKind, EventReminderAdmin[]][] {
  const groups = new Map<EventReminderKind, EventReminderAdmin[]>();
  for (const reminder of reminders) {
    const legs = groups.get(reminder.kind);
    if (legs) legs.push(reminder);
    else groups.set(reminder.kind, [reminder]);
  }
  return [...groups];
}

interface EventRemindersProps {
  reminders: EventReminderAdmin[];
  /** The switch as it currently stands in the form, not as it was saved. */
  remindersEnabled: boolean;
  localeDrift: string | null;
  /**
   * The start the editor is currently holding, as an ISO instant, or null while the
   * field is empty. Differs from a row's `starts_at` exactly when there is something
   * unsaved to preview.
   */
  startsAt: string | null;
  isPublished: boolean;
}

/** Read-only view of the reminder ledger the server derives from this event. */
export function EventReminders({
  reminders,
  remindersEnabled,
  localeDrift,
  startsAt,
  isPublished,
}: EventRemindersProps) {
  const { t } = useTranslation();
  const { formatDate } = useLocale();

  const editedStart = startsAt === null ? null : new Date(startsAt).getTime();

  // Null means "the saved time is still the truth". Anything else is the instant the
  // next save will schedule this leg for, which is the only way the editor can see the
  // recomputation before committing to it.
  const previewFor = (reminder: EventReminderAdmin): string | null => {
    // A leg that has gone out is not rescheduled by anything — its `starts_at` is the
    // start it fired against, so previewing it would caption a delivered broadcast with
    // a time it was never sent at, even with no edit in progress.
    if (hasFired(reminder)) return null;
    if (editedStart === null || Number.isNaN(editedStart)) return null;
    if (editedStart === new Date(reminder.starts_at).getTime()) return null;
    return new Date(
      editedStart - REMINDER_OFFSET_MS[reminder.kind],
    ).toISOString();
  };

  // The switches are live state, so this sentence flips before the save that makes it
  // true — which is the point: it explains why the rows below will not fire.
  let suppressedReasonKey: string | null = null;
  if (!remindersEnabled)
    suppressedReasonKey = "eventEditor.reminders.reason.disabled";
  else if (!isPublished)
    suppressedReasonKey = "eventEditor.reminders.reason.unpublished";

  return (
    <Paper
      component="section"
      variant="outlined"
      data-testid="event.reminders"
      sx={{
        padding: 3,
        borderRadius: `${RADIUS.lg}px`,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box>
        <Typography variant="H3" sx={{ color: "text.primary" }}>
          {t("eventEditor.reminders.title")}
        </Typography>
        <Typography variant="T3" sx={{ color: "text.muted", marginTop: 0.5 }}>
          {t("eventEditor.reminders.hint")}
        </Typography>
      </Box>

      {suppressedReasonKey ? (
        <Alert severity="info" variant="outlined">
          {t(suppressedReasonKey)}
        </Alert>
      ) : null}

      {reminders.length === 0 ? (
        <Typography variant="T2" sx={{ color: "text.secondary" }}>
          {t("eventEditor.reminders.none")}
        </Typography>
      ) : (
        groupByKind(reminders).map(([kind, legs]) => (
          <ReminderGroup
            key={kind}
            kind={kind}
            legs={legs}
            previewFor={previewFor}
          />
        ))
      )}

      {localeDrift ? (
        <Alert severity="warning" data-testid="event.reminders-drift">
          {t("eventEditor.reminders.drift", {
            date: formatDate(localeDrift, REMINDER_TIME_FORMAT),
          })}
        </Alert>
      ) : null}

      <Typography variant="T3" sx={{ color: "text.muted" }}>
        {t("eventEditor.reminders.cadenceNote")}
      </Typography>
      <Typography variant="T3" sx={{ color: "text.muted" }}>
        {t("eventEditor.reminders.channelNote")}
      </Typography>
      <Typography variant="T3" sx={{ color: "text.muted" }}>
        {t("eventEditor.reminders.localeNote")}
      </Typography>
    </Paper>
  );
}

/**
 * One rung of the ladder: its label once, and every row the server holds for it.
 *
 * Usually that is a single row. When a webinar is genuinely postponed it is two — the
 * broadcast that already went out, then the one now scheduled — stacked in the order
 * they trigger, so the editor reads the history and the plan as one line of the ladder.
 */
function ReminderGroup({
  kind,
  legs,
  previewFor,
}: {
  kind: EventReminderKind;
  legs: EventReminderAdmin[];
  previewFor: (reminder: EventReminderAdmin) => string | null;
}) {
  const { t } = useTranslation();

  return (
    <Box
      data-testid={`event.reminder-${kind}`}
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "minmax(110px, 2fr) minmax(0, 3fr)",
        },
        gap: { xs: 0.5, sm: 2 },
        alignItems: "baseline",
      }}
    >
      <Typography variant="T3" sx={{ color: "text.muted" }}>
        {t(`eventEditor.reminders.offset.${kind}`)}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {legs.map((leg, index) => (
          // Unique per row: the ledger is keyed on (slug, locale, kind, starts_at), so
          // within one kind the start it was computed from identifies the row.
          <ReminderLeg
            key={leg.starts_at}
            reminder={leg}
            previewAt={previewFor(leg)}
            testId={`event.reminder-${kind}-${index + 1}`}
            divided={index > 0}
          />
        ))}
      </Box>
    </Box>
  );
}

function ReminderLeg({
  reminder,
  previewAt,
  testId,
  divided,
}: {
  reminder: EventReminderAdmin;
  previewAt: string | null;
  testId: string;
  divided: boolean;
}) {
  const { t } = useTranslation();
  const { formatDate } = useLocale();

  return (
    <Box
      data-testid={testId}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        borderTop: divided ? "1px solid" : "none",
        borderColor: "divider",
        paddingTop: divided ? 1.5 : 0,
      }}
    >
      <Typography variant="T2" sx={{ color: "text.secondary" }}>
        {formatDate(previewAt ?? reminder.scheduled_at, REMINDER_TIME_FORMAT)}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.75,
          alignItems: "center",
        }}
      >
        {previewAt ? (
          <Chip
            size="small"
            variant="outlined"
            color="info"
            label={t("eventEditor.reminders.preview")}
          />
        ) : null}
        {/* The chips describe the schedule as saved; only the time above moves with
            the unsaved edit, because a leg that already went out stays gone. */}
        <Chip
          size="small"
          variant="outlined"
          color={STATUS_COLOR[reminder.status]}
          label={t(`eventEditor.reminders.status.${reminder.status}`)}
        />
        {reminder.channels.map((channel) => (
          <Chip
            key={channel}
            size="small"
            variant="outlined"
            label={t(`eventEditor.reminders.channel.${channel}`)}
          />
        ))}
        {reminder.push_device_count > 0 ? (
          <Chip
            size="small"
            variant="outlined"
            label={t("eventEditor.reminders.devices", {
              count: reminder.push_device_count,
            })}
          />
        ) : null}
        {reminder.email_recipient_count > 0 ? (
          <Chip
            size="small"
            variant="outlined"
            label={t("eventEditor.reminders.recipients", {
              count: reminder.email_recipient_count,
            })}
          />
        ) : null}
      </Box>
      {reminder.skipped_reason ? (
        <Typography variant="T3" sx={{ color: "text.muted" }}>
          {t(`eventEditor.reminders.reason.${reminder.skipped_reason}`)}
        </Typography>
      ) : null}
    </Box>
  );
}
